import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api, { API } from "../lib/api";
import BannerSlot from "../components/BannerSlot";
import { ArrowRight, Sparkles, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "", state: "", description: "", estimated_units: 1,
  });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.get(`/services/${slug}`).then(r => setService(r.data)).catch(() => setService(null));
    api.get(`/services/${slug}/photos`).then(r => setPhotos(r.data)).catch(() => setPhotos([]));
    setAiText(""); setAiOpen(false);
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/quotes", { ...form, service_slug: slug });
      toast.success("Quote submitted. Disposal dispatch will contact you within 24 hours.");
      setForm({ full_name: "", email: "", phone: "", city: "", state: "", description: "", estimated_units: 1 });
    } catch (err) {
      console.error("Quote submit failed:", err);
      toast.error("Could not submit. Please try again.");
    }
  };

  const runAI = async () => {
    if (!form.description || form.description.length < 10) {
      toast.error("Add a description (min 10 chars) first.");
      return;
    }
    setAiOpen(true); setAiText(""); setAiLoading(true);
    try {
      const resp = await fetch(`${API}/smart-intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: form.description, service_slug: slug }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          const data = ln.slice(6);
          if (data === "[DONE]") { setAiLoading(false); return; }
          if (data.startsWith("[ERROR]")) { setAiText(t => t + "\n" + data); setAiLoading(false); return; }
          setAiText(t => t + data.replaceAll("\\n", "\n"));
        }
      }
    } catch (e) {
      console.error("AI intake failed:", e);
      setAiText("AI intake currently unavailable. Submit the form and we'll review manually.");
    } finally {
      setAiLoading(false);
    }
  };

  if (!service) return <div className="max-w-7xl mx-auto px-6 py-20" data-testid="service-loading">Loading…</div>;

  const heroPhoto = photos[0];
  const galleryPhotos = photos.slice(1);

  // Normalise URLs: relative paths (e.g. /api/generated/foo.jpg) resolve against
  // REACT_APP_BACKEND_URL so they work in both preview and production environments.
  const abs = (u) => (u && u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL || ""}${u || ""}`);

  // JSON-LD: Service + ImageObject for every photo (Google Image Search ranking signal)
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": service.name,
        "description": service.summary,
        "provider": {
          "@type": "Organization",
          "name": "Available Junk Removal",
          "url": "https://availablejunkremoval.com",
          "telephone": "+1-918-407-3395",
        },
        "areaServed": { "@type": "Country", "name": ["United States", "Canada"] },
        "image": photos.map(p => abs(p.url)),
      },
      ...photos.map(p => ({
        "@type": "ImageObject",
        "contentUrl": abs(p.url),
        "name": p.title,
        "caption": p.caption,
        "description": p.caption,
        "creditText": "Available Junk Removal",
        "creator": { "@type": "Organization", "name": "Available Junk Removal" },
      })),
    ],
  };

  return (
    <>
      <Helmet>
        <title>{`${service.name} · Available Junk Removal`}</title>
        <meta name="description" content={service.summary} />
        <meta property="og:title" content={service.name} />
        <meta property="og:description" content={service.summary} />
        {heroPhoto && <meta property="og:image" content={abs(heroPhoto.url)} />}
        <link rel="canonical" href={`https://availablejunkremoval.com/service/${slug}`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="service-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: heroPhoto ? `url('${abs(heroPhoto.url)}')` : undefined }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1020]/85 via-[#0b1020]/70 to-[#f43f5e]/40" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-white">
          <span className="sticker" style={{ background: "#fde4b6", color: "#0b1020", borderColor: "#0b1020" }}>
            <Sparkles className="w-3.5 h-3.5" /> {service.category} Division
          </span>
          <h1 className="display-black text-5xl lg:text-7xl mt-6 max-w-4xl leading-[0.95]">
            {service.name}
          </h1>
          <p className="text-lg lg:text-xl text-[#fde4b6] mt-5 font-semibold">{service.tagline}</p>
          <p className="text-base lg:text-lg text-white/85 mt-5 max-w-2xl leading-relaxed">{service.summary}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#intake" className="btn-signal">Get a Quote <ArrowRight className="w-4 h-4" /></a>
            <a href="tel:9184073395" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }}>918-407-3395</a>
          </div>
          {heroPhoto && (
            <figcaption className="mt-10 max-w-2xl text-xs text-white/65 italic">
              {heroPhoto.caption}
            </figcaption>
          )}
        </div>
      </section>

      {/* GALLERY — SEO-rich photo grid with structured captions */}
      {galleryPhotos.length > 0 && (
        <section className="py-20 lg:py-28 bg-white" data-testid="service-gallery">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="sec-num"><Camera className="w-3.5 h-3.5" /> Field Gallery</div>
            <h2 className="display-black text-3xl lg:text-5xl mt-3 text-[#0b1020] max-w-3xl">
              How <span className="hl-coral">{service.name.replace("Available ", "")}</span> looks in the field.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
              {galleryPhotos.map((p, idx) => (
                <figure key={p.url} className="bento-card overflow-hidden flex flex-col" data-testid={`service-photo-${idx}`}>
                  <img
                    src={abs(p.url)}
                    alt={p.alt}
                    title={p.title}
                    loading="lazy"
                    className="w-full aspect-[16/10] object-cover"
                  />
                  <figcaption className="p-6 flex-1">
                    <div className="text-[11px] text-[#f43f5e] font-semibold uppercase tracking-wider">{p.title}</div>
                    <p className="text-sm text-[#4b5468] mt-2 leading-relaxed">{p.caption}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INTAKE */}
      <section id="intake" className="py-20 lg:py-28 bg-[#faf6ef]" data-testid="service-quote-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <div className="sec-num">Disposal Intake</div>
            <h2 className="display-black text-3xl lg:text-5xl text-[#0b1020] mt-3">
              Request dispatch.<br />
              <span className="hl-cobalt">Average response: under 24 hours.</span>
            </h2>

            <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="quote-form">
              <input className="input-federal" required placeholder="Full name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="quote-name" />
              <input className="input-federal" required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="quote-email" />
              <input className="input-federal" required placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="quote-phone" />
              <input className="input-federal" placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="quote-city" />
              <input className="input-federal" placeholder="State / Province" value={form.state} onChange={e => setForm({...form, state: e.target.value})} data-testid="quote-state" />
              <input className="input-federal" type="number" min={1} placeholder="Estimated units" value={form.estimated_units} onChange={e => setForm({...form, estimated_units: parseInt(e.target.value)||1})} data-testid="quote-units" />
              <textarea className="input-federal md:col-span-2" required rows={5} placeholder="Describe the asset, location, urgency, access constraints…" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="quote-description" />
              <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
                <button type="submit" className="btn-signal" data-testid="quote-submit">
                  Submit Intake <ArrowRight className="w-4 h-4" />
                </button>
                <button type="button" onClick={runAI} className="btn-ghost" data-testid="ai-intake-btn">
                  <Sparkles className="w-4 h-4" /> AI Quick-Read
                </button>
                <span className="text-[12px] text-[#4b5468]">No spam · Bonded carriers only</span>
              </div>
            </form>

            {aiOpen && (
              <div className="mt-8 bento-card p-6" data-testid="ai-output">
                <div className="text-[11px] uppercase tracking-wider text-[#f43f5e] font-semibold flex items-center gap-2">
                  Disposal AI Coordinator {aiLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                </div>
                <pre className="mt-3 text-sm text-[#0b1020] whitespace-pre-wrap font-sans">{aiText || "Analysing…"}</pre>
              </div>
            )}
          </div>

          <aside className="lg:col-span-2">
            <div className="bento-card bento-card-dark p-7">
              <div className="text-[11px] uppercase tracking-wider text-[#fde4b6] font-semibold">Why Available</div>
              <h3 className="display-black text-2xl mt-3 text-white">Affiliate-Backed Network</h3>
              <ul className="mt-5 space-y-3 text-sm text-white/85">
                <li className="flex gap-3"><span className="text-[#f43f5e] font-bold">▸</span> Bonded carriers in every state & province</li>
                <li className="flex gap-3"><span className="text-[#f43f5e] font-bold">▸</span> Same-week mobilisation in most metros</li>
                <li className="flex gap-3"><span className="text-[#f43f5e] font-bold">▸</span> Title transfer + scrap recycling included</li>
                <li className="flex gap-3"><span className="text-[#f43f5e] font-bold">▸</span> Volume pricing for 100+ units</li>
              </ul>
              <Link to="/large-jobs" className="btn-signal w-full justify-center mt-7">Large Job? Priority Channel</Link>
            </div>
            <div className="mt-6"><BannerSlot placement="sidebar" /></div>
          </aside>
        </div>
      </section>
    </>
  );
}
